import React, { useState } from 'react'
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles((theme) => ({
  label: {
    color: theme.palette.grey[600],
  },
  list: {
    height: 350,
    overflow: 'scroll',
  },
  filter: {
    borderRadius: 0,

    '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline':
      {
        border: 'none',
        borderBottom: `solid 1px ${theme.palette.divider}`,
      },
  },
}))

export type ChecklistProps<T> = {
  label: string
  options: T[]
  keyGetter: (item: T) => string
  labelGetter: (item: T) => string
  value: string[]
  isFilterable?: boolean
  onChange: (value: string[]) => void
}

export function Checklist<T>({
  label,
  options,
  keyGetter,
  labelGetter,
  value,
  isFilterable,
  onChange,
}: ChecklistProps<T>): React.ReactElement {
  const { t } = useTranslation()
  const classes = useStyles()
  const [filter, setFilter] = useState('')

  const toggleItem = (key: string) => {
    if (value.includes(key)) {
      onChange(value.filter((item) => item !== key))
    } else {
      onChange([...value, key])
    }
  }

  const filteredOptions = options.filter((item) =>
    labelGetter(item).toLowerCase().includes(filter.toLocaleLowerCase()),
  )

  return (
    <Box>
      <Typography className={classes.label}>{label}</Typography>

      <Paper variant="outlined">
        {isFilterable && (
          <TextField
            variant="outlined"
            placeholder={t('checklist.filter')}
            value={filter}
            onChange={(event) => setFilter(event.currentTarget.value)}
            InputProps={{ className: classes.filter }}
          />
        )}

        <List className={classes.list}>
          {filteredOptions.map((item) => {
            const key = keyGetter(item)

            return (
              <ListItem key={key} button dense onClick={() => toggleItem(key)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={value.includes(key)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={labelGetter(item)} />
              </ListItem>
            )
          })}
        </List>
      </Paper>
    </Box>
  )
}
